import * as React from 'react';

interface RecognitionResult {
  isFinal: boolean;
  [index: number]: { transcript: string };
}

interface RecognitionEvent {
  results: ArrayLike<RecognitionResult>;
  resultIndex: number;
}

interface BrowserSpeechRecognition {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  onresult: ((event: RecognitionEvent) => void) | null;
  onerror: ((event: { error: string }) => void) | null;
  onend: (() => void) | null;
  start(): void;
  stop(): void;
  abort(): void;
}

type RecognitionConstructor = new () => BrowserSpeechRecognition;

function recognitionConstructor(): RecognitionConstructor | undefined {
  if (typeof window === 'undefined') return undefined;
  const browser = window as Window & {
    SpeechRecognition?: RecognitionConstructor;
    webkitSpeechRecognition?: RecognitionConstructor;
  };
  return browser.SpeechRecognition ?? browser.webkitSpeechRecognition;
}

function recognitionError(error: string): string {
  switch (error) {
    case 'not-allowed':
    case 'service-not-allowed':
      return 'Microphone access was blocked. Open your browser site settings (usually beside the address bar), find Microphone and allow access for this site. Reload this page, then select the microphone button again. If access is still blocked, allow your browser to use the microphone in your device privacy settings. You can also type your message.';
    case 'audio-capture':
      return 'No microphone is available. Connect one and try again.';
    case 'no-speech':
      return 'No speech was heard. Try speaking closer to your microphone.';
    case 'network':
      return 'The browser could not reach its speech service. Try again or type your message.';
    default:
      return 'Speech recognition failed. Try again or type your message.';
  }
}

export function useAssistantSpeech(onTranscript: (text: string) => void) {
  const [listening, setListening] = React.useState(false);
  const [speakingId, setSpeakingId] = React.useState<string | null>(null);
  const [speechError, setSpeechError] = React.useState<string | null>(null);
  const recognitionRef = React.useRef<BrowserSpeechRecognition | null>(null);
  const utteranceRef = React.useRef<SpeechSynthesisUtterance | null>(null);
  const onTranscriptRef = React.useRef(onTranscript);
  onTranscriptRef.current = onTranscript;

  const recognitionAvailable = Boolean(recognitionConstructor());
  const playbackAvailable = typeof window !== 'undefined' &&
    'speechSynthesis' in window && 'SpeechSynthesisUtterance' in window;

  const stopListening = React.useCallback((discard = false) => {
    const recognition = recognitionRef.current;
    if (!recognition) return;
    if (discard) {
      recognition.onresult = null;
      recognition.onerror = null;
      recognition.onend = null;
      recognitionRef.current = null;
      setListening(false);
      recognition.abort();
    } else {
      recognition.stop();
    }
  }, []);

  const stopSpeaking = React.useCallback(() => {
    if (!utteranceRef.current || !playbackAvailable) return;
    utteranceRef.current.onend = null;
    utteranceRef.current.onerror = null;
    utteranceRef.current = null;
    window.speechSynthesis.cancel();
    setSpeakingId(null);
  }, [playbackAvailable]);

  const toggleListening = React.useCallback(() => {
    if (recognitionRef.current) {
      stopListening();
      return;
    }
    const Recognition = recognitionConstructor();
    if (!Recognition) return;
    stopSpeaking();
    setSpeechError(null);
    const recognition = new Recognition();
    recognition.lang = navigator.language || 'en-AU';
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.onresult = (event) => {
      if (recognitionRef.current !== recognition) return;
      // Results are cumulative within a session; interim words can be revised or removed.
      const transcript = Array.from(event.results)
        .map((result) => result[0]?.transcript.trim() ?? '')
        .filter(Boolean)
        .join(' ');
      onTranscriptRef.current(transcript);
    };
    recognition.onerror = (event) => {
      if (recognitionRef.current !== recognition) return;
      if (event.error !== 'aborted') setSpeechError(recognitionError(event.error));
    };
    recognition.onend = () => {
      if (recognitionRef.current !== recognition) return;
      recognitionRef.current = null;
      setListening(false);
    };
    recognitionRef.current = recognition;
    setListening(true);
    try {
      recognition.start();
    } catch {
      recognitionRef.current = null;
      setListening(false);
      setSpeechError('The microphone could not start. Check browser permissions and try again.');
    }
  }, [stopListening, stopSpeaking]);

  const toggleSpeaking = React.useCallback((id: string, content: string) => {
    if (!playbackAvailable) return;
    if (speakingId === id) {
      stopSpeaking();
      return;
    }
    stopSpeaking();
    setSpeechError(null);
    const utterance = new SpeechSynthesisUtterance(content.replaceAll('**', ''));
    utterance.lang = navigator.language || 'en-AU';
    utterance.onend = () => {
      if (utteranceRef.current === utterance) {
        utteranceRef.current = null;
        setSpeakingId(null);
      }
    };
    utterance.onerror = () => {
      if (utteranceRef.current === utterance) {
        utteranceRef.current = null;
        setSpeakingId(null);
        setSpeechError('This browser could not read the reply aloud.');
      }
    };
    utteranceRef.current = utterance;
    setSpeakingId(id);
    try {
      window.speechSynthesis.speak(utterance);
    } catch {
      utteranceRef.current = null;
      setSpeakingId(null);
      setSpeechError('This browser could not read the reply aloud.');
    }
  }, [playbackAvailable, speakingId, stopSpeaking]);

  React.useEffect(() => () => {
    const recognition = recognitionRef.current;
    if (recognition) {
      recognition.onresult = null;
      recognition.onerror = null;
      recognition.onend = null;
      recognition.abort();
      recognitionRef.current = null;
    }
    if (utteranceRef.current && playbackAvailable) {
      window.speechSynthesis.cancel();
      utteranceRef.current = null;
    }
  }, [playbackAvailable]);

  return {
    recognitionAvailable,
    playbackAvailable,
    listening,
    speakingId,
    speechError,
    toggleListening,
    stopListening,
    toggleSpeaking
  };
}
